<script setup>
// Google Ads > Ayarlar
//
// Baglanti uc adimda kuruluyor ve ekran hangi adimda oldugunu acikca
// gosteriyor:
//   1. Gelistirici jetonu  -> platform ayari, super admin giriyor
//   2. Google yetkisi      -> kullanici kendi Google hesabini bagliyor
//   3. Reklam hesabi       -> erisilebilir hesaplardan biri seciliyor
//
// Google onayindan donen yetki kodu adres cubugunda geliyor; aliyoruz,
// jetona ceviriyoruz ve adres cubugundan SILIYORUZ - kod tek kullanimlik
// olsa da adres gecmisinde durmasi gereksiz.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import GoogleAdsKabuk from './GoogleAdsKabuk.vue';
import { getir, gonder, hataMetni } from './kopru.js';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const baglanti = ref(null);
const yetkiAdresi = ref('');
const hesaplar = ref([]);
const seciliHesap = ref('');
const yoneticiId = ref('');
const yukleniyor = ref(true);
const islemde = ref(false);
const hata = ref('');

const accountId = computed(() => route.params.accountId);
const yetkiVar = computed(() => Boolean(baglanti.value?.baglanma_zamani));
const hazir = computed(() => baglanti.value?.bagli === true);

const durumuYukle = async () => {
  try {
    const veri = await getir(accountId.value, 'durum');
    baglanti.value = veri.baglanti;
    yetkiAdresi.value = veri.yetki_adresi || '';
    seciliHesap.value = veri.baglanti?.musteri_id || '';
    yoneticiId.value = veri.baglanti?.yonetici_id || '';
  } catch (e) {
    hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
  }
};

const hesaplariYukle = async () => {
  if (!yetkiVar.value) return;
  islemde.value = true;
  try {
    const veri = await getir(accountId.value, 'hesaplar');
    hesaplar.value = veri.hesaplar || [];
  } catch (e) {
    hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
  } finally {
    islemde.value = false;
  }
};

// Google'dan donen kodu isle ve adres cubugunu temizle.
const donusuIsle = async () => {
  const kod = route.query.google_ads_kod;
  const googleHata = route.query.google_ads_hata;

  if (googleHata) {
    hata.value = t('GOOGLE_ADS.YETKI_REDDEDILDI');
  } else if (kod) {
    islemde.value = true;
    try {
      const veri = await gonder(accountId.value, 'baglan', { code: kod });
      baglanti.value = veri.baglanti;
      useAlert(t('GOOGLE_ADS.YETKI_ALINDI'));
    } catch (e) {
      hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
    } finally {
      islemde.value = false;
    }
  }

  if (kod || googleHata) {
    router.replace({ query: {} });
  }
};

const yetkiyeGit = () => {
  if (!yetkiAdresi.value) return;
  window.location.href = yetkiAdresi.value;
};

const hesabiKaydet = async () => {
  islemde.value = true;
  hata.value = '';
  try {
    const veri = await gonder(accountId.value, 'kaydet', {
      musteri_id: seciliHesap.value,
      yonetici_id: yoneticiId.value,
    });
    baglanti.value = veri.baglanti;
    useAlert(t('GOOGLE_ADS.KAYDEDILDI'));
  } catch (e) {
    hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
  } finally {
    islemde.value = false;
  }
};

const baglantiyiKaldir = async () => {
  if (!window.confirm(t('GOOGLE_ADS.KALDIR_ONAY'))) return;

  islemde.value = true;
  try {
    const veri = await gonder(accountId.value, 'kaldir');
    baglanti.value = veri.baglanti;
    hesaplar.value = [];
    seciliHesap.value = '';
    useAlert(t('GOOGLE_ADS.KALDIRILDI'));
  } catch (e) {
    hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
  } finally {
    islemde.value = false;
  }
};

onMounted(async () => {
  await durumuYukle();
  await donusuIsle();
  await hesaplariYukle();
  yukleniyor.value = false;
});
</script>

<template>
  <GoogleAdsKabuk
    :baslik="t('GOOGLE_ADS.AYARLAR')"
    :baglanti-gerekli="false"
    :aralik-goster="false"
  >
    <div v-if="yukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="flex flex-col max-w-2xl gap-4">
      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <!-- Gelistirici jetonu PLATFORM ayari; kiracinin yapabilecegi bir
           sey yok. Bu yuzden yalnizca EKSIKSE gosteriliyor ve o zaman da
           "yoneticinizle gorusun" diyor. Jetonun kendisi hicbir zaman
           ekrana ya da API yanitina girmiyor. -->
      <section
        v-if="baglanti?.eksik_adim === 'gelistirici_jetonu'"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-amber-6 bg-n-amber-9/5"
      >
        <h2 class="text-sm font-medium text-n-amber-11">
          {{ t('GOOGLE_ADS.JETON_YOK_BASLIK') }}
        </h2>
        <p class="text-xs text-n-slate-11">
          {{ t('GOOGLE_ADS.JETON_YOK_ACIKLAMA') }}
        </p>
      </section>

      <!-- Adim 2: Google yetkisi -->
      <section class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
        <div class="flex items-center gap-2">
          <span
            class="grid rounded-full size-5 place-content-center text-xs"
            :class="
              yetkiVar
                ? 'bg-n-teal-9/20 text-n-teal-11'
                : 'bg-n-amber-9/20 text-n-amber-11'
            "
          >
            {{ yetkiVar ? '✓' : '1' }}
          </span>
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('GOOGLE_ADS.ADIM_YETKI') }}
          </h2>
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('GOOGLE_ADS.ADIM_YETKI_ACIKLAMA') }}
        </p>
        <p v-if="yetkiVar" class="text-xs text-n-slate-11">
          {{ t('GOOGLE_ADS.BAGLAYAN', { kisi: baglanti.baglayan || '—' }) }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            size="sm"
            :variant="yetkiVar ? 'faded' : 'solid'"
            icon="i-lucide-link"
            :label="yetkiVar ? t('GOOGLE_ADS.YENIDEN_BAGLAN') : t('GOOGLE_ADS.GOOGLE_BAGLA')"
            :disabled="islemde || !yetkiAdresi"
            @click="yetkiyeGit"
          />
          <Button
            v-if="yetkiVar"
            size="sm"
            variant="ghost"
            color="ruby"
            icon="i-lucide-unlink"
            :label="t('GOOGLE_ADS.KALDIR')"
            :disabled="islemde"
            @click="baglantiyiKaldir"
          />
        </div>
        <p v-if="!yetkiAdresi" class="text-xs text-n-amber-11">
          {{ t('GOOGLE_ADS.ISTEMCI_YOK') }}
        </p>
      </section>

      <!-- Adim 3: reklam hesabi -->
      <section
        v-if="yetkiVar"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
      >
        <div class="flex items-center gap-2">
          <span
            class="grid rounded-full size-5 place-content-center text-xs"
            :class="
              hazir
                ? 'bg-n-teal-9/20 text-n-teal-11'
                : 'bg-n-amber-9/20 text-n-amber-11'
            "
          >
            {{ hazir ? '✓' : '2' }}
          </span>
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('GOOGLE_ADS.ADIM_HESAP') }}
          </h2>
        </div>

        <label class="text-xs text-n-slate-11">
          {{ t('GOOGLE_ADS.HESAP_SEC') }}
          <select v-model="seciliHesap" class="!mb-0 mt-1 w-full text-sm">
            <option value="">{{ t('GOOGLE_ADS.SECINIZ') }}</option>
            <option v-for="h in hesaplar" :key="h.musteri_id" :value="h.musteri_id">
              {{ h.hesap_adi || h.musteri_id }} ({{ h.musteri_id }})
              {{ h.yonetici_mi ? ' — ' + t('GOOGLE_ADS.YONETICI') : '' }}
            </option>
          </select>
        </label>

        <label class="text-xs text-n-slate-11">
          {{ t('GOOGLE_ADS.YONETICI_ID') }}
          <input
            v-model="yoneticiId"
            type="text"
            class="!mb-0 mt-1 w-full text-sm"
            placeholder="123-456-7890"
          />
          <span class="block mt-1 text-xs text-n-slate-10">
            {{ t('GOOGLE_ADS.YONETICI_ACIKLAMA') }}
          </span>
        </label>

        <div class="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="solid"
            :label="t('GOOGLE_ADS.KAYDET')"
            :is-loading="islemde"
            :disabled="islemde || !seciliHesap"
            @click="hesabiKaydet"
          />
          <Button
            size="sm"
            variant="faded"
            icon="i-lucide-refresh-cw"
            :label="t('GOOGLE_ADS.HESAPLARI_YENILE')"
            :disabled="islemde"
            @click="hesaplariYukle"
          />
        </div>
      </section>

      <!-- Durum ozeti -->
      <section
        v-if="hazir"
        class="flex flex-col gap-1 p-4 text-xs border rounded-xl border-n-weak text-n-slate-11"
      >
        <span class="text-sm font-medium text-n-teal-11">
          {{ t('GOOGLE_ADS.BAGLI') }}
        </span>
        <span>{{ baglanti.hesap_adi }} · {{ baglanti.musteri_id_bicimli }}</span>
        <span v-if="baglanti.para_birimi">
          {{ baglanti.para_birimi }} · {{ baglanti.saat_dilimi }}
        </span>
        <span v-if="baglanti.son_hata" class="text-n-ruby-11">
          {{ baglanti.son_hata }}
        </span>
      </section>
    </div>
  </GoogleAdsKabuk>
</template>
